import { useEffect } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import { scrollToAnchor } from '@/lib/scrollToAnchor'
import { sectionFromPath } from '@/lib/sections'

/**
 * Управление прокруткой при клиентской навигации. Три задачи.
 *
 * 0. Адрес блока главной (/roi, /cases — см. src/lib/sections.ts). Это та же
 *    главная, и открыть её надо на этом блоке: и при прямом заходе по
 *    ссылке, и при переходе с другой страницы. Если прокрутку уже запустил
 *    клик по меню (state.scrolled), второй раз не стартуем.
 *
 * 1. `#anchor` в URL. Браузер сам не скроллит при SPA-переходе (в отличие
 *    от полной перезагрузки) — скроллим руками, с до-коррекцией после
 *    оседания layout (см. scrollToAnchor).
 *
 * 2. Переход на ДРУГУЮ страницу без якоря. Роутер меняет маршрут, но
 *    высоту прокрутки браузер оставляет прежнюю — и человек попадает в
 *    середину новой страницы. Наблюдалось на /en: со дна главной жмёшь
 *    «How it works» и видишь не заголовок, а хвост страницы с финальным
 *    CTA. Выглядит как сломанная страница, хотя весь контент на месте.
 *
 * Различаем переход и загрузку по ТИПУ навигации, а не по «первому
 * рендеру»: главная и внутренние страницы — разные поддеревья, при
 * переходе компонент монтируется заново, и любая проверка «это первый
 * рендер?» на реальном переходе даёт true. Проверено — сброс не срабатывал.
 *
 * PUSH — человек кликнул ссылку, читать начинают сверху.
 * POP — первая загрузка или кнопка «назад». Если в адресе блок (/roi) или
 * #якорь — открываем на нём: при первой загрузке иначе нельзя, и «назад»
 * на /#calculator вёл себя так же. Если якоря нет — позицию оставляем
 * браузеру, наверх не сбрасываем.
 */
export function ScrollToHash() {
  const { hash, pathname, state } = useLocation()
  const navType = useNavigationType()
  const alreadyScrolled = Boolean((state as { scrolled?: boolean } | null)?.scrolled)

  useEffect(() => {
    // Прокрутку уже запустил сам клик (пункт меню, логотип) — не мешаем.
    // Пометке верим только при клике: история браузера хранит state и после
    // перезагрузки, и при «назад» — там (POP) прокрутку никто не запускал.
    if (alreadyScrolled && navType !== 'POP') return

    const section = sectionFromPath(pathname)
    if (section && !hash) {
      scrollToAnchor(section)
      return
    }
    if (hash) {
      const id = hash.slice(1)
      if (id) scrollToAnchor(id)
      return
    }
    if (navType === 'PUSH') {
      // 'instant', а НЕ 'auto': по спецификации auto означает «взять
      // scroll-behavior у элемента», а в tokens.css у html стоит smooth.
      // С auto новая страница целую секунду ехала снизу вверх — замеряно:
      // 2978 → 1512 → 228 → 0. Открывать надо сверху, а не привозить туда.
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    }
  }, [hash, pathname, navType, alreadyScrolled])

  return null
}
